import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../../config";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [showOAuthButtons, setShowOAuthButtons] = useState(false);
  const [oauthProvider, setOauthProvider] = useState("");
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/start/google`);
      window.location.href = response.data.auth_url;
    } catch (err) {
      setError("Failed to initialize Google login. Please try again.");
    }
  };

  const handleOutlookLogin = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/start/outlook`,
        {},
        {
          withCredentials: true,
        }
      );
      window.location.href = response.data.auth_url;
    } catch (err) {
      setError("Failed to initialize Outlook login. Please try again.");
    }
  };

  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setShowOAuthButtons(false);
    setOauthProvider("");
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/auth/login`,
        {
          email: formData.email,
          password: formData.password,
        },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        }
      );

      Cookies.set("access_token", response.data.access_token, {
        expires: 10 / 24,
        path: "/",
        sameSite: "none",
        secure: true,
      });

      Cookies.set("refresh_token", response.data.refresh_token, {
        expires: 10 / 24,
        path: "/",
        sameSite: "none",
        secure: true,
      });

      navigate("/dashboard");
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || "Login failed. Please try again.";

      if (errorMessage.includes("uses Google authentication")) {
        toast.info("Please sign in with Google");
        setShowOAuthButtons(true);
        setOauthProvider("google");
      } else if (errorMessage.includes("uses Outlook authentication")) {
        toast.info("Please sign in with Outlook");
        setShowOAuthButtons(true);
        setOauthProvider("outlook");
      } else {
        toast.error("Invalid credentials. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1F9BFF1A] flex">
      <div className="w-full md:w-1/2 flex items-center justify-center">
        <div className="max-w-lg w-full space-y-4 bg-white p-4 md:p-8 rounded-2xl shadow-lg border border-gray-200 m-2 md:m-6">
          <div className="flex flex-col items-center justify-center p-4 md:p-8">
            <div className="self-start">
              <h2 className="text-3xl font-bold text-gray-800 mb-1">Sign in</h2>
              <p className="text-sm text-gray-600 mt-2">
                New user?{" "}
                <span
                  className="text-blue-400 cursor-pointer"
                  onClick={() => navigate("/register")}
                >
                  Create an account.
                </span>
              </p>
            </div>
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            {showOAuthButtons && (
              <div className="w-full mt-6 space-y-4">
                <p className="text-sm text-gray-600">Please sign in with:</p>
                {oauthProvider === "google" && (
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <img
                      src="https://www.google.com/favicon.ico"
                      alt="Google"
                      className="w-5 h-5"
                    />
                    Continue with Google
                  </button>
                )}
                {oauthProvider === "outlook" && (
                  <button
                    type="button"
                    onClick={handleOutlookLogin}
                    className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <img
                      src="https://www.microsoft.com/favicon.ico"
                      alt="Outlook"
                      className="w-5 h-5"
                    />
                    Continue with Outlook
                  </button>
                )}
              </div>
            )}
            <form onSubmit={handleSubmit} className="mt-6 space-y-4 w-full">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-500"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  className="block w-full py-0.6 border-b border-gray-300 focus:border-b-2 focus:border-blue-500 focus:outline-none"
                />
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-500 mt-3"
                >
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  className="block w-full py-0.6 border-b border-gray-300 focus:border-b-2 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="flex justify-between items-center pt-2">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm text-gray-600 hover:text-blue-500"
                >
                  Forgot Password?
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="py-2 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#1F9BFF] hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Signing in...
                    </>
                  ) : (
                    "Sign in"
                  )}
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>
              <div className="flex flex-col space-y-2">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <img
                    src="https://www.google.com/favicon.ico"
                    alt="Google"
                    className="w-5 h-5"
                  />
                  Continue with Google
                </button>
                <button
                  type="button"
                  onClick={handleOutlookLogin}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <img
                    src="https://www.microsoft.com/favicon.ico"
                    alt="Outlook"
                    className="w-5 h-5"
                  />
                  Continue with Outlook
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Right side - Blue section with text and logo */}
      <div className="hidden md:flex w-3/5 bg-[#1F9BFF1A] flex-col items-center justify-center p-12 text-white">
        <div className="space-y-6">
          <img src="/Efficia-01.svg" alt="Logo" className="h-15" />
          <h1 className="text-6xl font-bold text-blue-400 mb-4">
            {/* One tool for you to manage Email */}
            One tool to streamline your workflow
          </h1>
          <p className="text-lg text-gray-600">
            Empower your productivity with our Executive AI Agent – organize,
            prioritize, and make smarter decisions effortlessly.
          </p>
          <p className="text-lg text-gray-600">
            Access your account to manage your tasks and stay organized.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
